import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { AzureAuthService } from 'app/core/auth/azure-auth.service';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom, from } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _authService: AzureAuthService
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        this._fuseMockApiService.onGet('api/common/navigation').reply(() =>
            from(
                (async () => {
                    // Fill compact navigation children using the default navigation
                    this._compactNavigation.forEach((compactNavItem) => {
                        this._defaultNavigation.forEach((defaultNavItem) => {
                            if (defaultNavItem.id === compactNavItem.id) {
                                compactNavItem.children = cloneDeep(
                                    defaultNavItem.children
                                );
                            }
                        });
                    });

                    // Fill futuristic navigation children using the default navigation
                    this._futuristicNavigation.forEach((futuristicNavItem) => {
                        this._defaultNavigation.forEach((defaultNavItem) => {
                            if (defaultNavItem.id === futuristicNavItem.id) {
                                futuristicNavItem.children = cloneDeep(
                                    defaultNavItem.children
                                );
                            }
                        });
                    });

                    // Fill horizontal navigation children using the default navigation
                    this._horizontalNavigation.forEach((horizontalNavItem) => {
                        this._defaultNavigation.forEach((defaultNavItem) => {
                            if (defaultNavItem.id === horizontalNavItem.id) {
                                horizontalNavItem.children = cloneDeep(
                                    defaultNavItem.children
                                );
                            }
                        });
                    });

                    // Wait for the result of isAdmin() observable
                    const isAdmin = await firstValueFrom(
                        this._authService.isAdmin()
                    );

                    // Filter out 'results', 'planning' and 'admin' items if the user is not an admin
                    if (!isAdmin) {
                        const itemsToFilter = ['admin', 'results', 'planning'];

                        this._compactNavigation =
                            this._compactNavigation.filter(
                                (item) => !itemsToFilter.includes(item.id)
                            );
                        this._futuristicNavigation =
                            this._futuristicNavigation.filter(
                                (item) => !itemsToFilter.includes(item.id)
                            );
                        this._horizontalNavigation =
                            this._horizontalNavigation.filter(
                                (item) => !itemsToFilter.includes(item.id)
                            );
                    }

                    // Return the response
                    return [
                        200,
                        {
                            compact: cloneDeep(this._compactNavigation),
                            default: cloneDeep(this._defaultNavigation),
                            futuristic: cloneDeep(this._futuristicNavigation),
                            horizontal: cloneDeep(this._horizontalNavigation),
                        },
                    ];
                })()
            )
        );
    }
}
